'use client'

import { useState, useEffect } from 'react'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'
import { grantConsent, denyConsent } from '@/utils/analytics'

/** Çerez tercihleri bandını yeniden açmak için yayınlanan olay adı. */
export const COOKIE_SETTINGS_EVENT = 'borsaya:open-cookie-settings'

/** Footer gibi herhangi bir yerden çağrılabilir: bandı yeniden açar. */
export function openCookieSettings() {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))
}

export default function CookieConsent() {
  const lang = useLocale()
  const [visible, setVisible] = useState(false)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent')
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800)
      return () => clearTimeout(timer)
    }
    // Kayıtlı karar her yüklemede etiketlere yeniden bildirilir. "accepted" için
    // layout.tsx zaten erken update yapar; buradaki çağrı gtag geç hazır olursa
    // yedek görevi görür. "rejected" için çerez temizliği burada yapılır.
    if (consent === 'accepted') grantConsent()
    else denyConsent()
  }, [])

  // Ağustos 2026 denetimi: verilen onay geri alınamıyordu — tek çıkış yolu
  // localStorage'ı elle temizlemekti. Rızanın verildiği kadar kolay geri
  // alınabilmesi gerekiyor. Footer'daki "Çerez Tercihleri" bağlantısı bu olayı
  // tetikleyerek bandı yeniden açar.
  useEffect(() => {
    const reopen = () => {
      setClosing(false)
      setVisible(true)
    }
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen)
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen)
  }, [])

  function dismiss(accepted: boolean) {
    setClosing(true)
    localStorage.setItem('cookie_consent', accepted ? 'accepted' : 'rejected')
    if (accepted) grantConsent()
    else denyConsent()
    setTimeout(() => setVisible(false), 300)
  }

  if (!visible) return null

  const privacyPath = lang === 'tr' ? `/${lang}/gizlilik-politikasi` : `/${lang}/privacy`

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('cookie.title', lang)}
      className="fixed z-[55] bottom-16 md:bottom-6 left-0 right-0 flex justify-center px-3 md:px-6"
      style={{
        opacity: closing ? 0 : 1,
        transform: closing ? 'translateY(20px)' : 'translateY(0)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        animation: closing ? 'none' : 'cookieSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <div
        className="w-full max-w-2xl rounded-2xl shadow-2xl border overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-color)',
          backdropFilter: 'blur(16px)',
        }}
      >
        <div className="p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div
                className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-color) 12%, transparent)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                  <path d="M8.5 8.5v.01" />
                  <path d="M16 15.5v.01" />
                  <path d="M12 12v.01" />
                  <path d="M11 17v.01" />
                  <path d="M7 14v.01" />
                </svg>
              </div>
              <div className="min-w-0">
                <h3
                  className="text-[13px] font-semibold leading-tight"
                  style={{ color: 'var(--text-main)' }}
                >
                  {t('cookie.title', lang)}
                </h3>
                <p
                  className="text-[12px] leading-relaxed mt-1"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {t('cookie.text', lang)}{' '}
                  <a
                    href={privacyPath}
                    title={t('cookie.privacy', lang)}
                    className="underline underline-offset-2 hover:no-underline"
                    style={{ color: 'var(--accent-color)' }}
                  >
                    {t('cookie.privacy', lang)}
                  </a>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 ml-12 md:ml-0">
              <button
                onClick={() => dismiss(false)}
                className="text-[12px] font-medium py-2.5 px-5 rounded-lg border cursor-pointer transition-all duration-200 hover:brightness-95 active:scale-[0.97]"
                style={{
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-muted)',
                  backgroundColor: 'transparent',
                }}
              >
                {t('cookie.reject', lang)}
              </button>
              <button
                onClick={() => dismiss(true)}
                className="text-[12px] font-semibold py-2.5 px-6 rounded-lg text-white cursor-pointer transition-all duration-200 hover:brightness-110 hover:shadow-md active:scale-[0.97]"
                style={{ backgroundColor: 'var(--accent-color)' }}
              >
                {t('cookie.accept', lang)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
